import React, { useState, useEffect, useRef } from "react";
import Confetti from "react-confetti";
import { Helmet, HelmetProvider } from "react-helmet-async";
import BottomLogin from "./bottomLogin";
import { BottomSheetCustom } from "@cred/neopop-web/lib/components";
import {
  checkEventWinnerStatus,
  submitWinnerDetails,
} from "../Services/Service";
import { Crown } from "lucide-react";
import { Modal } from "react-bootstrap";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import UrlPopup from "../components/UrlPopup";

const WinnerForm = () => {
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [winnerStatus, setWinnerStatus] = useState(null);
  const [alreadyUpdated, setAlreadyUpdated] = useState(false);
  const [winnerStatusLoading, setWinnerStatusLoading] = useState(true);
  const [winnerStatusError, setWinnerStatusError] = useState(false);

  // State for URL popup
  const [showUrlPopup, setShowUrlPopup] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");
  const [isUrlLoading, setIsUrlLoading] = useState(false); // New state for URL popup loader

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // KYC Form states
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    isAbove18: 1, // 1 = above 18, 2 = below 18
    adhaarNumber: "",
    panNumber: "",
    bankAccountName: "",
    bankAccountNumber: "",
    confirmBankAccountNumber: "",
    ifscCode: "",
    accountType: "savings",
    mobile: "",
    // Guardian fields for users below 18
    guardianFirstName: "",
    guardianLastName: "",
    guardianEmail: "",
    guardianAdhaarNumber: "",
    guardianPanNumber: "",
    guardianBankAccountName: "",
    guardianIsAbove18: 1,
    guardianBankAccountNumber: "",
    confirmGuardianBankAccountNumber: "",
    guardianIfscCode: "",
    guardianAccountType: "savings",
    guardianMobile: "",
    // Confirmation checkbox
    confirmationCheckbox: false,
  });
  const [files, setFiles] = useState({
    adhaarImage: null,
    panImage: null,
    faugScreenshot: null,
    guardianAdhaarImage: null,
    guardianPanImage: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showKycForm, setShowKycForm] = useState(false);

  const divRef = useRef(null);
  const isLoggedIn = localStorage.getItem("auth")
    ? localStorage.getItem("auth")
    : "";

  const scrollToTop = () => {
    if (divRef.current) {
      divRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    scrollToTop();

    const pageParam = searchParams.get("page");

    const checkWinnerStatus = async () => {
      try {
        setWinnerStatusLoading(true);
        const response = await checkEventWinnerStatus();
        if (response?.data?.code === 200) {
          const winnerData = response.data.isEventWinner;
          console.log("Winner Data:", winnerData);
          setWinnerStatus(winnerData);
          setAlreadyUpdated(response.data.alreadyUpdated);
          setCurrentUrl(response.data.loginUrl);

          // If user is an event winner, then check login status
          if (winnerData?.isEventWinner === "Y") {
            // User is a winner, login overlay will be shown by user interaction
            // Don't automatically open login - wait for user to click
          }
          // If not a winner, winnerStatus will be set and access denied will show
        } else {
          setWinnerStatusError(true);
        }
      } catch (error) {
        console.error("Error checking winner status:", error);
        setWinnerStatusError(true);
      } finally {
        setWinnerStatusLoading(false);
      }
    };

    // Always check winner status first
    checkWinnerStatus();

    if (pageParam === "detail") {
      // setCurrentUrl(window.location.href);
      setShowUrlPopup(true);
      setIsUrlLoading(true); // Start loading when URL popup is shown
      // Simulate URL "loading" for 3 seconds
      setTimeout(() => {
        setIsUrlLoading(false);
      }, 3000);
    } else if (pageParam === "form") {
      setShowUrlPopup(false); // Ensure URL popup is not shown for 'form'
      // The KYC form will be shown based on winnerStatus and isLoggedIn
    }

    // Preload image for faster loading
    const preloadImage = () => {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = "https://appcdn.goqii.com/storeimg/50300_1759141846.png";
    };

    preloadImage();

    // Handle window resize for confetti
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    // Stop confetti after 5 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
    };
  }, [isLoggedIn, searchParams]); // Add searchParams to dependency array

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (!file) {
      setFiles((prev) => ({
        ...prev,
        [name]: null,
      }));
      return;
    }

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes

    if (file.size > maxSize) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "File size should be less than 5MB",
      }));
      return;
    }

    // Check file type - only JPEG and PNG are allowed
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const fileType = file.type.toLowerCase();

    if (!allowedTypes.includes(fileType)) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "Invalid file type. Only JPEG and PNG files are allowed.",
      }));
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [name]: file,
    }));

    // Clear error when user selects valid file
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Required text fields
    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = "Email is invalid";
    if (!formData.mobile.trim()) errors.mobile = "Mobile number is required";
    else if (!/^\d{10}$/.test(formData.mobile))
      errors.mobile = "Mobile number must be 10 digits";

    // Validate based on age
    if (formData.isAbove18 === 1) {
      // User is above 18 - validate personal details
      if (!formData.adhaarNumber.trim())
        errors.adhaarNumber = "Aadhaar number is required";
      else if (!/^\d{12}$/.test(formData.adhaarNumber))
        errors.adhaarNumber = "Aadhaar number must be 12 digits";
      // PAN number is now required
      if (!formData.panNumber.trim())
        errors.panNumber = "PAN number is required";
      // else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber))
      //   errors.panNumber = "PAN number format is invalid";
      if (!formData.bankAccountName.trim())
        errors.bankAccountName = "Bank account name is required";
      if (!formData.bankAccountNumber.trim())
        errors.bankAccountNumber = "Bank account number is required";
      if (!formData.confirmBankAccountNumber.trim())
        errors.confirmBankAccountNumber =
          "Please confirm your bank account number";
      else if (formData.bankAccountNumber !== formData.confirmBankAccountNumber)
        errors.confirmBankAccountNumber = "Bank account numbers do not match";
      if (!formData.ifscCode.trim()) errors.ifscCode = "IFSC code is required";
      // else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode))
      //   errors.ifscCode = "IFSC code format is invalid";

      // Required files for above 18
      if (!files.adhaarImage) errors.adhaarImage = "Aadhaar image is required";
      else if (files.adhaarImage && files.adhaarImage.size > 5 * 1024 * 1024)
        errors.adhaarImage = "Aadhaar image size should be less than 5MB";
      else if (files.adhaarImage) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(files.adhaarImage.type.toLowerCase())) {
          errors.adhaarImage =
            "Invalid file type. Only JPEG and PNG files are allowed.";
        }
      }
      // PAN image is now required
      if (!files.panImage) errors.panImage = "PAN image is required";
      else if (files.panImage && files.panImage.size > 5 * 1024 * 1024)
        errors.panImage = "PAN image size should be less than 5MB";
      else if (files.panImage) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(files.panImage.type.toLowerCase())) {
          errors.panImage =
            "Invalid file type. Only JPEG and PNG files are allowed.";
        }
      }
    } else if (formData.isAbove18 === 2) {
      // User is below 18 - validate guardian details
      if (!formData.guardianFirstName.trim())
        errors.guardianFirstName = "Guardian first name is required";
      if (!formData.guardianLastName.trim())
        errors.guardianLastName = "Guardian last name is required";
      if (!formData.guardianEmail.trim())
        errors.guardianEmail = "Guardian email is required";
      else if (!/\S+@\S+\.\S+/.test(formData.guardianEmail))
        errors.guardianEmail = "Guardian email is invalid";
      if (!formData.guardianMobile.trim())
        errors.guardianMobile = "Guardian mobile number is required";
      else if (!/^\d{10}$/.test(formData.guardianMobile))
        errors.guardianMobile = "Guardian mobile number must be 10 digits";
      if (!formData.guardianAdhaarNumber.trim())
        errors.guardianAdhaarNumber = "Guardian Aadhaar number is required";
      else if (!/^\d{12}$/.test(formData.guardianAdhaarNumber))
        errors.guardianAdhaarNumber =
          "Guardian Aadhaar number must be 12 digits";
      // Guardian PAN number is now required
      if (!formData.guardianPanNumber.trim())
        errors.guardianPanNumber = "Guardian PAN number is required";
      // else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.guardianPanNumber))
      // errors.guardianPanNumber = "Guardian PAN number format is invalid";
      if (!formData.guardianBankAccountName.trim())
        errors.guardianBankAccountName =
          "Guardian bank account name is required";
      if (!formData.guardianBankAccountNumber.trim())
        errors.guardianBankAccountNumber =
          "Guardian bank account number is required";
      if (!formData.confirmGuardianBankAccountNumber.trim())
        errors.confirmGuardianBankAccountNumber =
          "Please confirm guardian's bank account number";
      else if (
        formData.guardianBankAccountNumber !==
        formData.confirmGuardianBankAccountNumber
      )
        errors.confirmGuardianBankAccountNumber =
          "Guardian bank account numbers do not match";
      if (!formData.guardianIfscCode.trim())
        errors.guardianIfscCode = "Guardian IFSC code is required";
      // else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.guardianIfscCode))
      //   errors.guardianIfscCode = "Guardian IFSC code format is invalid";

      // Required files for below 18
      if (!files.guardianAdhaarImage)
        errors.guardianAdhaarImage = "Guardian Aadhaar image is required";
      else if (
        files.guardianAdhaarImage &&
        files.guardianAdhaarImage.size > 5 * 1024 * 1024
      )
        errors.guardianAdhaarImage =
          "Guardian Aadhaar image size should be less than 5MB";
      else if (files.guardianAdhaarImage) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (
          !allowedTypes.includes(files.guardianAdhaarImage.type.toLowerCase())
        ) {
          errors.guardianAdhaarImage =
            "Invalid file type. Only JPEG and PNG files are allowed.";
        }
      }
      // Guardian PAN image is now required
      if (!files.guardianPanImage)
        errors.guardianPanImage = "Guardian PAN image is required";
      else if (
        files.guardianPanImage &&
        files.guardianPanImage.size > 5 * 1024 * 1024
      )
        errors.guardianPanImage =
          "Guardian PAN image size should be less than 5MB";
      else if (files.guardianPanImage) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(files.guardianPanImage.type.toLowerCase())) {
          errors.guardianPanImage =
            "Invalid file type. Only JPEG and PNG files are allowed.";
        }
      }
    }

    // FAU-G screenshot is always required
    if (!files.faugScreenshot)
      errors.faugScreenshot = "FAU-G screenshot is required";
    else if (
      files.faugScreenshot &&
      files.faugScreenshot.size > 5 * 1024 * 1024
    )
      errors.faugScreenshot = "FAU-G screenshot size should be less than 5MB";
    else if (files.faugScreenshot) {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(files.faugScreenshot.type.toLowerCase())) {
        errors.faugScreenshot =
          "Invalid file type. Only JPEG and PNG files are allowed.";
      }
    }

    // Confirmation checkbox is required
    if (!formData.confirmationCheckbox)
      errors.confirmationCheckbox =
        "Please confirm that all information provided is true and correct";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const formDataToSend = new FormData();

      // Add basic fields (always required)
      formDataToSend.append("firstName", formData.firstName);
      formDataToSend.append("lastName", formData.lastName);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobile", formData.mobile);
      formDataToSend.append("isAbove18", formData.isAbove18);
      formDataToSend.append(
        "confirmationCheckbox",
        formData.confirmationCheckbox
      );

      // Add FAU-G screenshot (always required)
      if (files.faugScreenshot) {
        formDataToSend.append("faugScreenshot", files.faugScreenshot);
      }

      if (formData.isAbove18 === 1) {
        // User is above 18 - add personal details
        formDataToSend.append("adhaarNumber", formData.adhaarNumber);
        formDataToSend.append("panNumber", formData.panNumber);
        formDataToSend.append("bankAccountName", formData.bankAccountName);
        formDataToSend.append("bankAccountNumber", formData.bankAccountNumber);
        formDataToSend.append("ifscCode", formData.ifscCode);
        formDataToSend.append("accountType", formData.accountType);

        // Add personal files
        if (files.adhaarImage) {
          formDataToSend.append("adhaarImage", files.adhaarImage);
        }
        if (files.panImage) {
          formDataToSend.append("panImage", files.panImage);
        }
      } else if (formData.isAbove18 === 2) {
        // User is below 18 - add guardian details
        formDataToSend.append("guardianFirstName", formData.guardianFirstName);
        formDataToSend.append("guardianLastName", formData.guardianLastName);
        formDataToSend.append("guardianEmail", formData.guardianEmail);
        formDataToSend.append("guardianMobile", formData.guardianMobile);
        formDataToSend.append("guardianIsAbove18", formData.guardianIsAbove18);
        formDataToSend.append(
          "guardianAdhaarNumber",
          formData.guardianAdhaarNumber
        );
        formDataToSend.append("guardianPanNumber", formData.guardianPanNumber);
        formDataToSend.append(
          "guardianBankAccountName",
          formData.guardianBankAccountName
        );
        formDataToSend.append(
          "guardianBankAccountNumber",
          formData.guardianBankAccountNumber
        );
        formDataToSend.append("guardianIfscCode", formData.guardianIfscCode);
        formDataToSend.append(
          "guardianAccountType",
          formData.guardianAccountType
        );

        // Add guardian files
        if (files.guardianAdhaarImage) {
          formDataToSend.append(
            "guardianAdhaarImage",
            files.guardianAdhaarImage
          );
        }
        if (files.guardianPanImage) {
          formDataToSend.append("guardianPanImage", files.guardianPanImage);
        }
      }

      const response = await submitWinnerDetails(formDataToSend);

      if (response?.data?.code === 200 || response?.data?.code === 201) {
        setSubmitSuccess(true);
      } else {
        setSubmitError(
          response.data.msg || "Submission failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitError("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 1. Define a function to stop the event from bubbling up
  const handleScrollInteraction = (e) => {
    // This prevents the event from propagating up to the BottomSheetCustom
    e.stopPropagation();
    // For touch moves, sometimes e.nativeEvent.stopImmediatePropagation() is needed
    // e.nativeEvent.stopImmediatePropagation();
  };

  // Function modified to prioritize the generic hybrid/native methods for simplicity and compatibility
  const openExternalLink = (url) => {
    console.log("Attempting to open URL:", url);

    // 1. Try native JavaScript Interface call (specific to the app/game)
    if (window.Android && typeof window.Android.openInBrowser === "function") {
      window.Android.openInBrowser(url);
      return;
    }

    // 2. Try iOS-specific WebKit Message Handler call
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.openBrowser
    ) {
      window.webkit.messageHandlers.openBrowser.postMessage(url);
      return;
    }

    // 3. Try generic hybrid method (preferred for most frameworks like Cordova/Capacitor)
    // The '_system' target tells the WebView host to break out and open the link
    // in the device's system default browser (which might be Chrome).
    const attemptedSystemOpen = window.open(url, "_system");

    // 4. Final Web fallback for standard browser environments or if _system failed
    if (!attemptedSystemOpen || attemptedSystemOpen.closed) {
      window.open(url, "_blank");
    }
  };

  const handleCloseUrlPopup = () => {
    setShowUrlPopup(false);
    // Optionally redirect or perform other actions after closing the URL popup
    // navigate("/home", { replace: true });
  };

  return (
    <div
      className="container-fluid"
      ref={divRef}
      style={{
        width: "100%",
        padding: "0 10px",
        boxSizing: "border-box",
        marginTop: "48px",
        marginBottom: "6rem",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        paddingTop: "20px",
      }}
    >
      <HelmetProvider>
        <Helmet>
          <title>FauG Bharat League - Winner Congratulations!</title>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
          />
        </Helmet>
      </HelmetProvider>

      {showUrlPopup && (
        <UrlPopup
          url={currentUrl}
          onClose={handleCloseUrlPopup}
          isLoading={isUrlLoading}
        />
      )}

      {/* Confetti Effect */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
        />
      )}

      {/* Show Page Not Found if winnerData is N */}
      {!winnerStatusLoading && winnerStatus === "N" && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            minHeight: "50vh",
            padding: "20px",
          }}
        >
          <div
            className="text-center p-5 rounded-3"
            style={{
              maxWidth: "500px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "2px solid rgba(255, 193, 7, 0.3)",
              color: "#fff",
            }}
          >
            <div style={{ fontSize: "4rem", marginBottom: "20px" }}>🔍</div>
            <h2
              className="text-warning mb-3"
              style={{ fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}
            >
              Page Not Found
            </h2>
            <p
              className="text-light mb-4"
              style={{ fontSize: "clamp(1rem, 2.5vw, 1.2rem)" }}
            >
              Sorry, the page you're looking for doesn't exist or has been
              moved.
              <br />
              Please check the URL or try navigating to a different page.
            </p>
            <button
              onClick={() => (window.location.href = "/home")}
              className="btn btn-warning fw-bold px-4 py-3"
              style={{
                borderRadius: "20px",
                border: "none",
                background: "linear-gradient(45deg, #ffc107 0%, #ff8c00 100%)",
                boxShadow: "0 4px 12px rgba(255, 193, 7, 0.4)",
              }}
            >
              Go Back To Home
            </button>
          </div>
        </div>
      )}

      {/* Show Access Denied for API errors */}
      {!winnerStatusLoading && winnerStatusError && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            minHeight: "50vh",
            padding: "20px",
          }}
        >
          <div
            className="text-center p-5 rounded-3"
            style={{
              maxWidth: "500px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "2px solid rgba(255, 193, 7, 0.3)",
              color: "#fff",
            }}
          >
            <div style={{ fontSize: "4rem", marginBottom: "20px" }}>🔒</div>
            <h2
              className="text-warning mb-3"
              style={{ fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}
            >
              Access Denied
            </h2>
            <p
              className="text-light mb-4"
              style={{ fontSize: "clamp(1rem, 2.5vw, 1.2rem)" }}
            >
              Sorry, you don't have permission to access this page.
              <br />
              This page is only for FAU-G Bharat League Season 1 winners.
            </p>
            <button
              onClick={() => window.history.back()}
              className="btn btn-warning fw-bold px-4 py-3"
              style={{
                borderRadius: "20px",
                border: "none",
                background: "linear-gradient(45deg, #ffc107 0%, #ff8c00 100%)",
                boxShadow: "0 4px 12px rgba(255, 193, 7, 0.4)",
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {/* Show loading while checking winner status */}
      {winnerStatusLoading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            minHeight: "50vh",
            padding: "20px",
          }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-warning mb-3"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-light" style={{ fontSize: "1.1rem" }}>
              Verifying access...
            </p>
          </div>
        </div>
      )}

      {/* Login Overlay - Only show if user is a winner but not logged in */}
      {!isLoggedIn &&
        !isLoginOpen &&
        !winnerStatusLoading &&
        winnerStatus === "Y" && (
          <div
            style={{
              width: "100%",
              height: "100%",
              position: "fixed",
              zIndex: 99,
              top: 0,
              left: 0,
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
            }}
            className="d-flex justify-content-center align-items-center"
          >
            <div
              className="text-center p-5 rounded-3"
              style={{
                maxWidth: "400px",
                backgroundColor: "rgba(255, 255, 255)",
                color: "#000",
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
              }}
            >
              <h2 className="text-3xl md:text-4xl font-extrabold mb-4 animate-pulse text-dark">
                Please Log In
              </h2>
              <p className="mb-0">Dear Titan,</p>
              <p className="text-xl md:text-xl text-white-500 mb-8">
                Log into IceSpice to complete your KYC.
              </p>
              <button
                onClick={() => setIsLoginOpen(true)}
                className="btnPrimary py-2 px-6 rounded-lg text-white font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                style={{
                  background: "linear-gradient(45deg, #4F46E5, #8B5CF6)",
                  border: "none",
                }}
              >
                Log In
              </button>
            </div>
          </div>
        )}

      {/* Main Card - Only show if user is a winner and logged in */}
      {isLoggedIn && !winnerStatusLoading && winnerStatus === "Y" && (
        <div
          className="text-white mx-auto position-relative"
          style={{
            maxWidth: "min(500px, 95vw)",
            width: "min(500px, 95vw)",
            margin: "0 auto",
            boxSizing: "border-box",
            borderRadius: "20px",
            background: "rgba(0, 0, 0, 0.85)",
            backdropFilter: "blur(10px)",
            zIndex: 1,
            paddingBottom: "20px",
          }}
        >
          <div className="mb-3 d-flex justify-content-center">
            {!imageLoaded && !imageError && (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: "100%",
                  height: "120px",
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "10px",
                  border: "2px dashed rgba(255, 193, 7, 0.3)",
                }}
              >
                <div className="text-center">
                  <div
                    className="spinner-border text-warning mb-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-light mb-0" style={{ fontSize: "0.9rem" }}>
                    Loading banner...
                  </p>
                </div>
              </div>
            )}
            {imageError && (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: "100%",
                  height: "120px",
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "10px",
                  border: "2px dashed rgba(255, 193, 7, 0.3)",
                }}
              >
                <div className="text-center">
                  <span style={{ fontSize: "2rem" }}>⚠️</span>
                  <p className="text-light mb-0" style={{ fontSize: "0.9rem" }}>
                    Image failed to load
                  </p>
                </div>
              </div>
            )}
            <img
              src="https://appcdn.goqii.com/storeimg/50300_1759141846.png"
              alt="SARC Banner"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "10px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                display: imageLoaded ? "block" : "none",
              }}
              className="img-fluid"
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </div>

          {/* Congratulations Section */}
          <div className="p-3">
            {/* Congratulations Header with Trophy Icons */}
            <div className="d-flex align-items-center justify-content-center mb-3">
              <span style={{ fontSize: "1.5rem", marginRight: "8px" }}>🏆</span>
              <h2
                className="text-warning mb-0 fw-bold text-center"
                style={{ fontSize: "clamp(1rem, 3vw, 2rem)" }}
              >
                CONGRATULATIONS!
              </h2>
              <span style={{ fontSize: "1.5rem", marginLeft: "8px" }}>🏆</span>
            </div>
            {/* Celebration Messages */}
            <div className="mb-3 text-center">
              <p
                className="text-info mb-2"
                style={{
                  fontSize: "clamp(1.2rem, 2.5vw, 1.1rem)",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              >
                TITANS
              </p>
            </div>
            {/* KYC Form Button */}
            <div
              onClick={() => {
                setShowUrlPopup(true);
              }}
              className="p-2 border border-warning mb-4 text-center rounded-3"
            >
              Are you unable to upload files ?
            </div>
            <div className="text-center mb-4">
              {alreadyUpdated === "N" && (
                <>
                  <p
                    className="text-light mb-2"
                    style={{
                      fontSize: "clamp(0.8rem, 2vw, 1rem)",
                      fontWeight: "normal",
                      marginBottom: "0",
                    }}
                  >
                    Please click on the button below to fill in KYC details for
                    transferring rewards to you.
                  </p>
                  <button
                    className="btn btn-warning fw-bold px-4 py-3 shadow-lg"
                    style={{
                      fontSize: "clamp(0.9rem, 3vw, 1.1rem)",
                      borderRadius: "20px",
                      border: "none",
                      background:
                        "linear-gradient(45deg, #ffc107 0%, #ff8c00 100%)",
                      boxShadow: "0 4px 12px rgba(255, 193, 7, 0.4)",
                      minWidth: "250px",
                    }}
                    onClick={() => setShowKycForm(true)}
                  >
                    Click To Fill KYC
                  </button>
                </>
              )}

              {/* <button
                className="btn btn-secondary"
                onClick={() =>
                  openExternalLink("https://staging.icespice.com/winnerForm")
                }
              >
                open form
              </button> */}
              {alreadyUpdated === "Y" && (
                <div
                  className="text-center mb-4 mx-auto"
                  style={{ maxWidth: "400px" }}
                >
                  {/* Success Alert Box */}
                  <div
                    className="alert alert-success p-3 rounded-3 shadow-sm"
                    role="alert"
                  >
                    <span
                      className="alert-heading mb-1"
                      style={{ color: "black !important" }}
                    >
                      ✅ KYC Completed Successfully!
                    </span>
                    <p className="fw-bold mb-0">
                      You have already submitted your details.
                    </p>
                  </div>

                  {/* Support Contact Message */}
                  <p className="text-muted small mt-2">
                    If you need to update your details, please contact support
                    at:
                    <br />
                    <a
                      href="mailto:support@dot9games.com"
                      className="fw-bold text-decoration-none"
                    >
                      support@dot9games.com
                    </a>
                  </p>
                </div>
              )}
            </div>
            {/* Important Information Section */}
            <div
              className="mx-auto mb-4"
              style={{
                maxWidth: "400px",
                background: "rgba(0, 0, 0, 0.3)",
                borderRadius: "15px",
                padding: "1rem",
                border: "2px solid rgba(255, 193, 7, 0.2)",
              }}
            >
              <h6
                className="text-warning mb-3 fw-bold text-center"
                style={{ fontSize: "clamp(0.85rem, 2vw, 1rem)" }}
              >
                ℹ️ Important Information
              </h6>
              <ul
                className="text-light"
                style={{
                  fontSize: "clamp(0.75rem, 1.8vw, 0.95rem)",
                  lineHeight: "1.4",
                  marginBottom: "0",
                  paddingLeft: "1.2rem",
                }}
              >
                <li>KYC is mandatory for cash distribution to Titan Players</li>
                <li>
                  If you are below 18 , Then you have to fill the details of
                  your guardian and Prize money will be transferred to your
                  guardian's bank account.
                </li>
                <li>Please have your contact details and documents ready</li>
                <li>Complete within 7 days to claim your rewards</li>
              </ul>
            </div>
          </div>
          {/* Achievement Badges */}
          <div
            className="d-flex justify-content-center gap-2 flex-wrap"
            style={{
              maxWidth: "min(350px, 90vw)",
              margin: "0 auto",
              padding: "0 10px",
              marginBottom: "20px",
            }}
          >
            <div
              className="text-center px-2 px-md-3 py-2 rounded-pill fw-bold d-flex align-items-center justify-content-center flex-fill"
              style={{
                fontSize: "clamp(0.7rem, 1.5vw, 0.9rem)",
                background: "linear-gradient(135deg, #ffc107 0%, #ff8c00 100%)",
                color: "#000",
                minWidth: "70px",
                maxWidth: "110px",
                boxShadow: "0 3px 10px rgba(255, 193, 7, 0.4)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              🏆 WINNER
            </div>
            <div
              className="text-center px-2 px-md-3 py-2 rounded-pill fw-bold d-flex align-items-center justify-content-center flex-fill"
              style={{
                fontSize: "clamp(0.7rem, 1.5vw, 0.9rem)",
                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                color: "#fff",
                minWidth: "70px",
                maxWidth: "110px",
                boxShadow: "0 3px 10px rgba(40, 167, 69, 0.4)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              👑 TITAN
            </div>
            <div
              className="text-center px-2 px-md-3 py-2 rounded-pill fw-bold d-flex align-items-center justify-content-center flex-fill"
              style={{
                fontSize: "clamp(0.7rem, 1.5vw, 0.9rem)",
                background: "linear-gradient(135deg, #6f42c1 0%, #007bff 100%)",
                color: "#fff",
                minWidth: "70px",
                maxWidth: "110px",
                boxShadow: "0 3px 10px rgba(111, 66, 193, 0.4)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              ⭐ SEASON 1
            </div>
          </div>
        </div>
      )}

      {/* KYC Form Bottom Sheet */}
      <Modal
        show={showKycForm}
        onHide={() => setShowKycForm(false)}
        centered // Centers the modal vertically on the screen
        size="lg" // Makes the modal larger for better form visibility
        keyboard={false} // Optional: Prevents closing with the escape key
        contentClassName="bg-dark text-light" // Apply dark background and light text to the modal content itself
        style={{
          "--bs-modal-bg": "#001f2b",
          borderRadius: "20px",
          // Removed padding from here
        }} // Custom dark background color for the modal body/container
      >
        <div
          style={{
            backgroundColor: "#001f2b",
            padding: "30px",
            width: "100%",
            maxWidth:
              "min(500px, 95vw)" /* Ensure responsiveness and max width */,
            minWidth: "300px" /* Minimum width for mobile */,
            maxHeight: "90vh",
            overflowY: "auto",
            position: "relative",
            lineHeight: "1.5",
            touchAction: "pan-y",
            onScroll: { handleScrollInteraction },
            onTouchMove: { handleScrollInteraction },
            margin: "0 auto", // Center the content within the bottom sheet
            padding: "20px 20px",
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => setShowKycForm(false)}
            style={{
              position: "absolute",
              top: "15px",
              right: "15px",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#ffffffff",
              zIndex: 100 /* Ensure it's above other content */,
            }}
          >
            ×
          </button>

          {/* Success Message */}
          {submitSuccess && (
            <div className="text-center mb-4">
              <div style={{ fontSize: "3rem", marginBottom: "15px" }}>✅</div>
              <h3 className="text-success mb-3">KYC Submitted Successfully!</h3>
              <p className="text-muted">
                Your KYC details have been submitted. You will receive your
                rewards soon.
              </p>
              <button
                onClick={() => setShowKycForm(false)}
                className="btn btn-success"
              >
                Close
              </button>
            </div>
          )}

          {/* KYC Form */}
          {!submitSuccess && (
            <div
              style={{
                backgroundColor: "#001f2b !important",
                borderRadius: "8px",
              }}
            >
              <h2 className="text-center mb-4 text-light">KYC Details Form</h2>

              {/* Age Question */}
              <div className="text-center mb-4">
                <h5 className="text-warning mb-3">
                  🤔 First, let us know your age:
                </h5>
                <div className="row justify-content-center">
                  <div className="col-md-8">
                    <div className="card bg-dark border-warning">
                      <div className="card-body text-center">
                        <h6 className="text-warning mb-2">
                          Are you below 18 years old?
                        </h6>
                        <p className="text-light small mb-3">
                          If yes, we'll need your guardian's details such as
                          Aadhaar, PAN, and bank information.
                          <br />
                          <strong className="text-warning">
                            💰 Your prize money will be transferred to your
                            guardian's bank account.
                          </strong>
                          <br />
                          If no, you'll have to provide your own personal
                          details.
                        </p>
                        <div className="form-check d-inline-block">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="ageCheckbox"
                            checked={formData.isAbove18 === 2}
                            onChange={(e) => {
                              handleInputChange({
                                target: {
                                  name: "isAbove18",
                                  value: e.target.checked ? 2 : 1,
                                },
                              });
                            }}
                          />
                          <label
                            className="form-check-label text-warning"
                            htmlFor="ageCheckbox"
                          >
                            Yes, I am below 18 years old
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {submitError && (
                <div className="alert alert-danger mb-3">{submitError}</div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Personal Information */}
                  <div className="col-12">
                    <h5 className="text-primary mb-3">Personal Information</h5>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formErrors.firstName ? "is-invalid" : ""
                      }`}
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                    />
                    {formErrors.firstName && (
                      <div className="invalid-feedback">
                        {formErrors.firstName}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formErrors.lastName ? "is-invalid" : ""
                      }`}
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                    />
                    {formErrors.lastName && (
                      <div className="invalid-feedback">
                        {formErrors.lastName}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className={`form-control ${
                        formErrors.email ? "is-invalid" : ""
                      }`}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email address"
                    />
                    {formErrors.email && (
                      <div className="invalid-feedback">{formErrors.email}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Mobile Number *</label>
                    <input
                      type="tel"
                      className={`form-control ${
                        formErrors.mobile ? "is-invalid" : ""
                      }`}
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder="Enter 10-digit mobile number"
                      maxLength="10"
                    />
                    {formErrors.mobile && (
                      <div className="invalid-feedback">
                        {formErrors.mobile}
                      </div>
                    )}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">FAU-G Screenshot *</label>
                    <input
                      type="file"
                      className={`form-control ${
                        formErrors.faugScreenshot ? "is-invalid" : ""
                      }`}
                      name="faugScreenshot"
                      onChange={handleFileChange}
                      accept="image/jpeg,image/jpg,image/png"
                    />
                    <small className="text-muted">
                      <strong>Note:</strong> Maximum file size is 5MB. Only JPEG
                      and PNG files are allowed.
                    </small>
                    {formErrors.faugScreenshot && (
                      <div className="invalid-feedback">
                        {formErrors.faugScreenshot}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className={`form-check-input ${
                          formErrors.isAbove18 ? "is-invalid" : ""
                        }`}
                        style={{ width: "1.5em", height: "1.5em" }}
                        id="isAbove18"
                        name="isAbove18"
                        checked={formData.isAbove18 === 2}
                        onChange={(e) => {
                          handleInputChange({
                            target: {
                              name: "isAbove18",
                              value: e.target.checked ? 2 : 1,
                            },
                          });
                        }}
                      />{" "}
                      <label
                        className="form-check-label px-2"
                        htmlFor="isAbove18"
                        style={{ fontSize: "1rem" }}
                      >
                        {" "}
                        I am below 18 years old (Guardian/Parent's details
                        required)
                      </label>
                      {formErrors.isAbove18 && (
                        <div className="invalid-feedback">
                          {formErrors.isAbove18}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Conditional Instructions */}
                  <div className="col-12 mb-4">
                    {formData.isAbove18 === 1 && (
                      <div className="alert alert-info">
                        <h6 className="mb-2">
                          📋 <strong>Personal Details Required:</strong>
                        </h6>
                        <p className="mb-2">
                          Please provide your personal details for direct reward
                          transfer:
                        </p>
                        <ul className="mb-0 small">
                          <li>Your Aadhaar and PAN details</li>
                          <li>Your bank account information</li>
                          <li>Your personal document images</li>
                        </ul>
                      </div>
                    )}
                    {formData.isAbove18 === 2 && (
                      <div className="alert alert-warning">
                        <h6 className="mb-2">
                          👨‍👩‍👧‍👦{" "}
                          <strong>Guardian/Parent's Details Required:</strong>
                        </h6>
                        <p className="mb-2">
                          Since you are below 18, please provide your
                          guardian/Parent's details for reward transfer:
                        </p>
                        <div className="alert alert-danger mb-3">
                          <strong>💰 Important:</strong> Your prize money will
                          be transferred to your guardian/Parent's bank account.
                        </div>
                        <ul className="mb-0 small">
                          <li>
                            <strong>Your details:</strong> First Name, Last
                            Name, Email, Mobile, FAU-G Screenshot
                          </li>
                          <li>
                            <strong>Guardian's details:</strong> First Name,
                            Last Name, Email, Mobile Number, Aadhaar, PAN, Bank
                            Account, Document Images
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Conditional Fields Based on Age */}
                  {formData.isAbove18 === 1 && (
                    <>
                      {/* Personal Details for Above 18 */}
                      <div className="col-12 mt-4">
                        <h5 className="text-primary mb-3">
                          Personal Document Information
                        </h5>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Aadhaar Number *</label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.adhaarNumber ? "is-invalid" : ""
                          }`}
                          name="adhaarNumber"
                          value={formData.adhaarNumber}
                          onChange={handleInputChange}
                          placeholder="Enter 12-digit Aadhaar number"
                          maxLength="12"
                        />
                        {formErrors.adhaarNumber && (
                          <div className="invalid-feedback">
                            {formErrors.adhaarNumber}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">PAN Number *</label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.panNumber ? "is-invalid" : ""
                          }`}
                          name="panNumber"
                          value={formData.panNumber}
                          onChange={handleInputChange}
                          placeholder="Enter PAN number"
                          style={{ textTransform: "uppercase" }}
                        />
                        {formErrors.panNumber && (
                          <div className="invalid-feedback">
                            {formErrors.panNumber}
                          </div>
                        )}
                      </div>

                      <div className="col-12 mb-3">
                        <label className="form-label">
                          Bank Account Name *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.bankAccountName ? "is-invalid" : ""
                          }`}
                          name="bankAccountName"
                          value={formData.bankAccountName}
                          onChange={handleInputChange}
                          placeholder="Enter bank account holder name"
                        />
                        {formErrors.bankAccountName && (
                          <div className="invalid-feedback">
                            {formErrors.bankAccountName}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Bank Account Number *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.bankAccountNumber ? "is-invalid" : ""
                          }`}
                          name="bankAccountNumber"
                          value={formData.bankAccountNumber}
                          onChange={handleInputChange}
                          placeholder="Enter bank account number"
                        />
                        {formErrors.bankAccountNumber && (
                          <div className="invalid-feedback">
                            {formErrors.bankAccountNumber}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Confirm Bank Account Number *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.confirmBankAccountNumber
                              ? "is-invalid"
                              : ""
                          }`}
                          name="confirmBankAccountNumber"
                          value={formData.confirmBankAccountNumber}
                          onChange={handleInputChange}
                          placeholder="Re-enter bank account number"
                        />
                        {formErrors.confirmBankAccountNumber && (
                          <div className="invalid-feedback">
                            {formErrors.confirmBankAccountNumber}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">IFSC Code *</label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.ifscCode ? "is-invalid" : ""
                          }`}
                          name="ifscCode"
                          value={formData.ifscCode}
                          onChange={handleInputChange}
                          placeholder="Enter IFSC code"
                          style={{ textTransform: "uppercase" }}
                        />
                        {formErrors.ifscCode && (
                          <div className="invalid-feedback">
                            {formErrors.ifscCode}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Account Type *</label>
                        <select
                          className="form-control"
                          name="accountType"
                          value={formData.accountType}
                          onChange={handleInputChange}
                        >
                          <option value="savings">Savings</option>
                          <option value="current">Current</option>
                        </select>
                      </div>

                      {/* Personal File Uploads */}
                      <div className="col-12 mt-4">
                        <h5 className="text-primary mb-3">
                          Personal Document Uploads
                        </h5>
                        <div className="alert alert-info mb-3">
                          <small>
                            <strong>📁 File Upload Guidelines:</strong> Maximum
                            file size is 5MB per image. Supported formats: JPEG,
                            PNG only
                          </small>
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Aadhaar Image *</label>
                        <input
                          type="file"
                          className={`form-control ${
                            formErrors.adhaarImage ? "is-invalid" : ""
                          }`}
                          name="adhaarImage"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/jpg,image/png"
                        />
                        <small className="text-muted">
                          <strong>Note:</strong> Only JPEG and PNG files are
                          allowed.
                        </small>
                        {formErrors.adhaarImage && (
                          <div className="invalid-feedback">
                            {formErrors.adhaarImage}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">PAN Image *</label>
                        <input
                          type="file"
                          className={`form-control ${
                            formErrors.panImage ? "is-invalid" : ""
                          }`}
                          name="panImage"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/jpg,image/png"
                        />
                        <small className="text-muted">
                          <strong>Note:</strong> Only JPEG and PNG files are
                          allowed.
                        </small>
                        {formErrors.panImage && (
                          <div className="invalid-feedback">
                            {formErrors.panImage}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {formData.isAbove18 === 2 && (
                    <>
                      {/* Guardian Details for Below 18 */}
                      <div className="col-12 mt-4">
                        <h5 className="text-primary mb-3">
                          Guardian/Parent's Information
                        </h5>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's First Name *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.guardianFirstName ? "is-invalid" : ""
                          }`}
                          name="guardianFirstName"
                          value={formData.guardianFirstName}
                          onChange={handleInputChange}
                          placeholder="Enter guardian first name"
                        />
                        {formErrors.guardianFirstName && (
                          <div className="invalid-feedback">
                            {formErrors.guardianFirstName}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's Last Name *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.guardianLastName ? "is-invalid" : ""
                          }`}
                          name="guardianLastName"
                          value={formData.guardianLastName}
                          onChange={handleInputChange}
                          placeholder="Enter guardian last name"
                        />
                        {formErrors.guardianLastName && (
                          <div className="invalid-feedback">
                            {formErrors.guardianLastName}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's Email *
                        </label>
                        <input
                          type="email"
                          className={`form-control ${
                            formErrors.guardianEmail ? "is-invalid" : ""
                          }`}
                          name="guardianEmail"
                          value={formData.guardianEmail}
                          onChange={handleInputChange}
                          placeholder="Enter guardian email address"
                        />
                        {formErrors.guardianEmail && (
                          <div className="invalid-feedback">
                            {formErrors.guardianEmail}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's Mobile Number *
                        </label>
                        <input
                          type="tel"
                          className={`form-control ${
                            formErrors.guardianMobile ? "is-invalid" : ""
                          }`}
                          name="guardianMobile"
                          value={formData.guardianMobile}
                          onChange={handleInputChange}
                          placeholder="Enter guardian mobile number"
                          maxLength="10"
                        />
                        {formErrors.guardianMobile && (
                          <div className="invalid-feedback">
                            {formErrors.guardianMobile}
                          </div>
                        )}
                      </div>

                      <div className="col-12 mt-4">
                        <h5 className="text-primary mb-3">
                          Guardian/Parent's Document Information
                        </h5>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's Aadhaar Number *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.guardianAdhaarNumber ? "is-invalid" : ""
                          }`}
                          name="guardianAdhaarNumber"
                          value={formData.guardianAdhaarNumber}
                          onChange={handleInputChange}
                          placeholder="Enter guardian 12-digit Aadhaar number"
                          maxLength="12"
                        />
                        {formErrors.guardianAdhaarNumber && (
                          <div className="invalid-feedback">
                            {formErrors.guardianAdhaarNumber}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's PAN Number *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.guardianPanNumber ? "is-invalid" : ""
                          }`}
                          name="guardianPanNumber"
                          value={formData.guardianPanNumber}
                          onChange={handleInputChange}
                          placeholder="Enter guardian PAN number"
                          style={{ textTransform: "uppercase" }}
                        />
                        {formErrors.guardianPanNumber && (
                          <div className="invalid-feedback">
                            {formErrors.guardianPanNumber}
                          </div>
                        )}
                      </div>

                      <div className="col-12 mb-3 fs-7">
                        <div className="alert alert-info mb-3">
                          <strong>💰 Bank Transfer Information:</strong>
                          <small>
                            {" "}
                            Your prize money will be transferred to the
                            guardian/Parent's bank account details provided
                            below.
                          </small>
                        </div>
                      </div>

                      <div className="col-12 mb-3">
                        <label className="form-label">
                          Guardian/Parent's Bank Account Name *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.guardianBankAccountName
                              ? "is-invalid"
                              : ""
                          }`}
                          name="guardianBankAccountName"
                          value={formData.guardianBankAccountName}
                          onChange={handleInputChange}
                          placeholder="Enter guardian bank account holder name"
                        />
                        {formErrors.guardianBankAccountName && (
                          <div className="invalid-feedback">
                            {formErrors.guardianBankAccountName}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's Bank Account Number *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.guardianBankAccountNumber
                              ? "is-invalid"
                              : ""
                          }`}
                          name="guardianBankAccountNumber"
                          value={formData.guardianBankAccountNumber}
                          onChange={handleInputChange}
                          placeholder="Enter guardian bank account number"
                        />
                        {formErrors.guardianBankAccountNumber && (
                          <div className="invalid-feedback">
                            {formErrors.guardianBankAccountNumber}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Confirm Guardian/Parent's Bank Account Number *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.confirmGuardianBankAccountNumber
                              ? "is-invalid"
                              : ""
                          }`}
                          name="confirmGuardianBankAccountNumber"
                          value={formData.confirmGuardianBankAccountNumber}
                          onChange={handleInputChange}
                          placeholder="Re-enter guardian bank account number"
                        />
                        {formErrors.confirmGuardianBankAccountNumber && (
                          <div className="invalid-feedback">
                            {formErrors.confirmGuardianBankAccountNumber}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's IFSC Code *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.guardianIfscCode ? "is-invalid" : ""
                          }`}
                          name="guardianIfscCode"
                          value={formData.guardianIfscCode}
                          onChange={handleInputChange}
                          placeholder="Enter guardian IFSC code"
                          style={{ textTransform: "uppercase" }}
                        />
                        {formErrors.guardianIfscCode && (
                          <div className="invalid-feedback">
                            {formErrors.guardianIfscCode}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's Account Type *
                        </label>
                        <select
                          className="form-control"
                          name="guardianAccountType"
                          value={formData.guardianAccountType}
                          onChange={handleInputChange}
                        >
                          <option value="savings">Savings</option>
                          <option value="current">Current</option>
                        </select>
                      </div>

                      {/* Guardian File Uploads */}
                      <div className="col-12 mt-4">
                        <h5 className="text-primary mb-3">
                          Guardian/Parent's Document Uploads
                        </h5>
                        <div className="alert alert-info mb-3">
                          <small>
                            <strong>📁 File Upload Guidelines:</strong> Maximum
                            file size is 5MB per image. Supported formats: JPEG,
                            PNG only
                          </small>
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's Aadhaar Image *
                        </label>
                        <input
                          type="file"
                          className={`form-control ${
                            formErrors.guardianAdhaarImage ? "is-invalid" : ""
                          }`}
                          name="guardianAdhaarImage"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/jpg,image/png"
                        />
                        <small className="text-muted">
                          <strong>Note:</strong> Only JPEG and PNG files are
                          allowed.
                        </small>
                        {formErrors.guardianAdhaarImage && (
                          <div className="invalid-feedback">
                            {formErrors.guardianAdhaarImage}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Guardian/Parent's PAN Image *
                        </label>
                        <input
                          type="file"
                          className={`form-control ${
                            formErrors.guardianPanImage ? "is-invalid" : ""
                          }`}
                          name="guardianPanImage"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/jpg,image/png"
                        />
                        <small className="text-muted">
                          <strong>Note:</strong> Only JPEG and PNG files are
                          allowed.
                        </small>
                        {formErrors.guardianPanImage && (
                          <div className="invalid-feedback">
                            {formErrors.guardianPanImage}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Confirmation Checkbox */}
                  <div className="col-12 mb-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className={`form-check-input ${
                          formErrors.confirmationCheckbox ? "is-invalid" : ""
                        }`}
                        id="confirmationCheckbox"
                        name="confirmationCheckbox"
                        checked={formData.confirmationCheckbox || false}
                        onChange={(e) => {
                          handleInputChange({
                            target: {
                              name: "confirmationCheckbox",
                              value: e.target.checked,
                            },
                          });
                        }}
                        style={{ width: "1.2em", height: "1.2em" }}
                      />
                      <label
                        className="form-check-label text-light"
                        htmlFor="confirmationCheckbox"
                        style={{ fontSize: "0.95rem", lineHeight: "1.4" }}
                      >
                        <strong>
                          I confirm that all the information I have provided
                          above is true and correct to the best of my knowledge.
                          Any falsification of data will result in forfeiting
                          the reward.
                        </strong>
                      </label>
                      {formErrors.confirmationCheckbox && (
                        <div className="invalid-feedback">
                          {formErrors.confirmationCheckbox}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    onClick={() => {
                      setShowUrlPopup(true);
                    }}
                    className="p-2 border border-warning text-center rounded-3"
                  >
                    Are you unable to upload files ? <br /> If yes, Click here
                  </div>
                  {/* Submit Button */}
                  <div className="col-12 text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-warning btn-lg px-5 py-3"
                      disabled={isSubmitting}
                      style={{
                        borderRadius: "25px",
                        minWidth: "200px",
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit KYC"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </Modal>

      {/* Bottom Login Sheet */}
      <BottomSheetCustom
        open={isLoginOpen}
        handleClose={() => setIsLoginOpen(!isLoginOpen)}
        maxHeight="700px"
      >
        <BottomLogin onClose={() => setIsLoginOpen(!isLoginOpen)} />
      </BottomSheetCustom>
    </div>
  );
};

export default WinnerForm;
